import HomeIcon from "@assets/svg/HomeIcon.svg?react"
import { useTranslation } from "react-i18next"
import {
	CustomDivider,
	DirectPaymentContainer,
	DirectPaymentSubtitle,
	DirectPaymentTextContainer,
	DirectPaymentTitle,
	HeaderSection,
	ReservationInfoWrapper,
} from "./styles"
import CustomSelect from "@Common/CustomSelect"
import { Box, Grid } from "@mui/material"
import CustomDatePicker from "@Common/CustomDatePicker"
import { Toggle } from "@components"
import useBreakPoinst from "@hooks/useBreakPoinst"
import { useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useRecoilValue } from "recoil"
import { reservationDetails } from "@store/index"
import { useGetUserDetail, useTemplate } from "@services"
import {
	isCheckOutExtended,
	propertyHasGuestBook,
	shouldWarnOffPlatformExtension,
} from "@utils"

interface IProps {
	ReservationInfoData: {
		checkIn: string | undefined
		checkOut: string | undefined
		propertyName: string | undefined
		reservationId: string | undefined
	}
	dataList: {
		value: string
		label: string
	}[]
}
export default function ReservationInfo({
	ReservationInfoData,
	dataList,
}: IProps) {
	const { t } = useTranslation()
	const { isPhone } = useBreakPoinst()
	const navigate = useNavigate()
	const { watch, setValue } = useFormContext()
	const reservationDetailsValue = useRecoilValue(reservationDetails)
	// A booking's check-out moving later turns this edit into an extension — a
	// second, linked booking for just the extra nights — and this toggle is what
	// decides whether THAT extension bills the guest through Msool. It lives here,
	// directly under the dates, matching where bookingInformationForm puts the
	// same toggle (right below the price) in the create drawer.
	const [isUserPaymentActive, setIsUserPaymentActive] = useState(
		reservationDetailsValue?.isDirectPaymentAccepted
	)
	useEffect(() => {
		setIsUserPaymentActive(reservationDetailsValue?.isDirectPaymentAccepted)
	}, [])
	const { data: userData } = useGetUserDetail()
	const isPaymentEnabled = (userData?.result as any)?.isUserPaymentActive
	// Direct payment requires the reservation's property to have a guest-book
	// template (sent with the payment link) — mirroring the backend's per-property
	// check, so a guest book on another property doesn't enable it here.
	const { getTemplatesQuery } = useTemplate()
	const { data: templates } = getTemplatesQuery()
	const selectedPropertyId =
		watch("propertyId") ?? reservationDetailsValue?.propertyId
	const selectedPropertyHasGuestBook = propertyHasGuestBook(
		templates,
		selectedPropertyId ? Number(selectedPropertyId) : undefined
	)
	// The warning promises the guest "a payment link for the extra nights", so it
	// must not appear unless there ARE extra nights: only a check-out moved LATER
	// than the booking's own turns this edit into an extension. Without this gate
	// it fired on any edit of an off-platform booking with the toggle on — a
	// phone-number fix, a name correction — promising a link that was never coming.
	const checkOutExtended = isCheckOutExtended(
		ReservationInfoData.checkOut,
		watch("checkOut")
	)
	// The closest honest signal for "was the PARENT stay settled through Msool":
	// isDirectPaymentAccepted on the loaded reservation. An MS-588 booking the
	// host collects for themselves reads false here — see src/utils/directPayment.ts.
	const showOffPlatformWarning = shouldWarnOffPlatformExtension(
		isUserPaymentActive,
		reservationDetailsValue?.isDirectPaymentAccepted,
		checkOutExtended
	)

	return (
		<ReservationInfoWrapper>
			<div>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<HeaderSection>
							<HomeIcon />
							<p>{t("MANUALRESERVATION.PROPERTYINFORMATION")}</p>
						</HeaderSection>
					</Grid>
					<Grid item xs={12} md={6}>
						<CustomSelect
							data={dataList ?? []}
							label={t("COMMON.PROPERTY")}
							name='propertyId'
							registerOptions={{
								required: {
									value: true,
									message: t("COMMON.REQUIRED"),
								},
							}}
						/>
						<div className='reservationNum'>
							<p>
								<span>{t("NEW_LOCKS.RESERVATION_NUMBER")} :</span>{" "}
								<span>{ReservationInfoData.reservationId}</span>
							</p>
						</div>
					</Grid>
					<CustomDivider />
					<Grid item xs={12}>
						<HeaderSection>
							<HomeIcon />
							<p>{t("MANUALRESERVATION.TIMEANDDATEOFRESERVATION")}</p>
						</HeaderSection>
					</Grid>
					<Grid item xs={12} md={6}>
						<CustomDatePicker
							disablePast
							name='checkIn'
							label={t("MANUALRESERVATION.BOOKINGSTARTDATE")}
							format='YYYY-MM-DD'
							registerOptions={{
								required: {
									value: true,
									message: t(
										"MANUALRESERVATION.PLEASESELECTTHESTARTDATEOFTHERESERVATION"
									),
								},
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<CustomDatePicker
							disablePast
							name='checkOut'
							label={t("MANUALRESERVATION.BOOKINGENDDATE")}
							format='YYYY-MM-DD'
							registerOptions={{
								required: {
									value: true,
									message: t(
										"MANUALRESERVATION.PLEASESELECTTHEENDDATEOFTHERESERVATION"
									),
								},
							}}
						/>
					</Grid>
					<CustomDivider />
					<Grid item xs={12}>
						<DirectPaymentContainer $isPhone={isPhone}>
							<DirectPaymentTextContainer $isPhone={isPhone}>
								<DirectPaymentTitle $isPhone={isPhone}>
									{t("MANUALRESERVATION.DIRECT_PAYMENT_RECEIPT")}
								</DirectPaymentTitle>
								<DirectPaymentSubtitle $isPhone={isPhone}>
									{t("MANUALRESERVATION.PAYMENT_LINK_DESCRIPTION")}
								</DirectPaymentSubtitle>
							</DirectPaymentTextContainer>
							{/* Keep the toggle visible even without a welcome-book template,
							    but disable it — the reason is shown in the alert below. */}
							{isPaymentEnabled ? (
								<Toggle
									disabled={!selectedPropertyHasGuestBook}
									checked={
										selectedPropertyHasGuestBook ? isUserPaymentActive : false
									}
									onChange={(event, checked) => {
										if (!selectedPropertyHasGuestBook) return
										setIsUserPaymentActive(checked)
										setValue("isUserPaymentActive", checked)
									}}
								/>
							) : null}
						</DirectPaymentContainer>
						{isPaymentEnabled && !selectedPropertyHasGuestBook && (
							<Box
								sx={{
									display: "flex",
									flexDirection: isPhone ? "column" : "row",
									alignItems: isPhone ? "stretch" : "center",
									justifyContent: "space-between",
									gap: "12px",
									mt: "12px",
									border: "1px solid #FCD34D",
									backgroundColor: "#FFFBEB",
									borderRadius: "12px",
									padding: "16px",
								}}
							>
								<Box sx={{ fontSize: "13px", color: "#92803F", lineHeight: "20px" }}>
									{t("MANUALRESERVATION.NO_TEMPLATE_ALERT")}
								</Box>
								<Box
									component="button"
									type="button"
									onClick={() => navigate("/messaging")}
									sx={{
										whiteSpace: "nowrap",
										backgroundColor: "#5B3FFF",
										color: "#fff",
										fontWeight: 700,
										fontSize: "13px",
										borderRadius: "8px",
										padding: "10px 18px",
										border: "none",
										cursor: "pointer",
										"&:hover": { backgroundColor: "#4930CC" },
									}}
								>
									{t("MANUALRESERVATION.CREATE_TEMPLATE")}
								</Box>
							</Box>
						)}
						{showOffPlatformWarning && (
							<Box
								sx={{
									fontSize: 12,
									lineHeight: "18px",
									color: "#6B7280",
									mt: "10px",
									px: "2px",
								}}
							>
								{t("MANUALRESERVATION.EXTENSION_OFF_PLATFORM_WARNING")}
							</Box>
						)}
					</Grid>
				</Grid>
				<CustomDivider />{" "}
			</div>
		</ReservationInfoWrapper>
	)
}
